import { formatTokenCount } from '../utils/formatTokenCount';
import { findSessionModel, sessionModelReference } from '../utils/sessionModel';
import { i18nService } from '../services/i18n';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { setSelectedModel } from '../store/slices/modelSlice';
import { coworkService } from '../services/cowork';
import { configService } from '../services/config';
import { getTargetClient, relayService } from '../services/relay';
import { findRelayModel, relayModelKey, relayProviderLabel } from '../services/relayModelKey';
import {
  CAPABILITY_TONE_CLASS,
  getModelCapabilityLabels,
  isPrimaryChatModel,
} from '../utils/modelCapabilityLabels';

interface ModelSelectorProps {
  dropdownDirection?: 'up' | 'down';
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ dropdownDirection = 'down' }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const selectionVersion = React.useRef(0), selecting = React.useRef(false);
  const [saving, setSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const allModels = useSelector((state: RootState) => state.model.availableModels);
  const currentSession = useSelector((state: RootState) => state.cowork.currentSession);
  const selectedDesktopClientId = useSelector((state: RootState) => state.cowork.selectedDesktopClientId);

  // Cowork 选择器只展示主对话模型,辅助模型(纯生图/纯 TTS/STT 等)由 SDK 路由自动调用,不显示给用户。
  // isPrimary 缺失视为 true → 兼容旧 platform / 自定义 provider。
  // 极端情况(全是副模型)退回完整列表,避免选择器空着。
  const availableModels = React.useMemo(() => {
    const primary = allModels.filter((m) => isPrimaryChatModel(m));
    return primary.length > 0 ? primary : allModels;
  }, [allModels]);

  // 会话内以 session.modelId 为唯一事实源；无会话时才使用全局默认模型。
  // 这样切换模型只影响当前会话，切到别的会话或返回新建页时不会串模型。
  const effectiveModel = React.useMemo(() => {
    if (!currentSession?.modelId) return selectedModel;
    if (currentSession.origin === 'desktop') {
      return findRelayModel(allModels, currentSession.modelId)
        ?? { id: currentSession.modelId, name: currentSession.modelId };
    }
    return findSessionModel(allModels, currentSession.modelId) ?? { id: currentSession.modelId, name: currentSession.modelId };
  }, [currentSession?.modelId, currentSession?.origin, allModels, selectedModel]);

  React.useEffect(() => {
    /** 错误卡只能打开当前会话的选择器；打开不改模型、更不自动发送任务。 */
    const openForSession = (event: Event) => {
      const request = (event as CustomEvent<{ sessionId?: string }>).detail;
      if (!currentSession || request?.sessionId !== currentSession.id || !availableModels.length) return;
      setIsOpen(true); triggerRef.current?.focus();
    };
    window.addEventListener('bomate:choose-model', openForSession);
    return () => window.removeEventListener('bomate:choose-model', openForSession);
  }, [currentSession?.id, availableModels.length]);

  // 点击外部区域关闭下拉框
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  React.useEffect(() => {
    selectionVersion.current++; selecting.current = false;
    setSaving(false); setSaveFailed(false); setIsOpen(false);
    return () => { selectionVersion.current++; };
  }, [currentSession?.id, selectedDesktopClientId]);

  /** 分别保存桌面会话、云端会话与新会话默认模型；失败保留选项，迟到回执不影响新会话。 */
  const handleModelSelect = async (model: typeof availableModels[0]) => {
    if (selecting.current) return;
    selecting.current = true; setSaving(true); setSaveFailed(false);
    const request = ++selectionVersion.current;
    try {
      let confirmed = true;
      if (currentSession && !currentSession.id.startsWith('temp-')) {
        if (currentSession.origin === 'desktop') {
          const targetClientId = getTargetClient(currentSession.id);
          if (!targetClientId) throw new Error(i18nService.t('coworkModelSaveFailed'));
          await relayService.setSessionModel(targetClientId, currentSession.id, relayModelKey(model));
        } else {
          confirmed = await coworkService.setSessionModel(currentSession.id, sessionModelReference(model));
        }
      } else {
        if (!selectedDesktopClientId) await configService.setDefaultModel(model.id);
        if (request !== selectionVersion.current) return;
        dispatch(setSelectedModel(model));
      }
      if (request !== selectionVersion.current) return;
      if (confirmed) setIsOpen(false);
      else setSaveFailed(true);
    } catch { if (request === selectionVersion.current) setSaveFailed(true); }
    finally {
      if (request === selectionVersion.current) { selecting.current = false; setSaving(false); }
    }
  };

  // 如果没有可用模型，显示提示
  if (availableModels.length === 0) {
    return (
      <div className="px-3 py-1.5 rounded-xl dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkTextSecondary text-claude-textSecondary text-sm">
        暂无可用模型
      </div>
    );
  }

  const dropdownPositionClass = dropdownDirection === 'up'
    ? 'bottom-full mb-1'
    : 'top-full mt-1';

  return (
    <div ref={containerRef} className="relative cursor-pointer">
      <button
        ref={triggerRef}
        type="button"
        aria-label={i18nService.t('selectModel')}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-1.5 rounded-xl dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:text-claude-darkText text-claude-text transition-colors cursor-pointer ${isOpen ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover' : ''}`}
      >
        <span className="font-medium text-sm">{effectiveModel.name}</span>
        <ChevronDownIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
      </button>

      {isOpen && (
        <div className={`absolute ${dropdownPositionClass} w-80 max-w-[calc(100vw-2rem)] dark:bg-claude-darkSurface bg-claude-surface rounded-xl popover-enter shadow-popover z-50 dark:border-claude-darkBorder border-claude-border border overflow-hidden`}>
          {saveFailed && <p role="status" className="px-4 py-2 text-xs text-amber-700 dark:text-amber-300">{i18nService.t('coworkModelSaveFailed')}</p>}
          <div className="max-h-64 overflow-y-auto">
          {availableModels.map((model) => (
            <button
              key={`${model.provider || ''}/${model.id}`}
              type="button"
              disabled={saving}
              onClick={() => void handleModelSelect(model)}
              className={`w-full px-4 py-2.5 text-left dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover flex items-center justify-between transition-colors ${
                model.id === effectiveModel.id && model.provider === effectiveModel.provider ? 'dark:bg-claude-darkSurfaceHover/50 bg-claude-surfaceHover/50' : ''
              }`}
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm">{model.name}</span>
                  {getModelCapabilityLabels(model).map((label) => (
                    <span
                      key={label.tone}
                      className={`text-[10px] px-1.5 py-0.5 rounded-md leading-none ${CAPABILITY_TONE_CLASS[label.tone]}`}
                    >
                      {label.text}
                    </span>
                  ))}
                </div>
                <span className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  {model.provider && `${relayProviderLabel(model.provider)} · `}
                  {i18nService.t('modelContextWindow')}: {' '}
                  {typeof model.maxContext === 'number' && Number.isSafeInteger(model.maxContext) && model.maxContext > 0
                    ? formatTokenCount(model.maxContext)
                    : i18nService.t('coworkContextUnknown')}
                </span>
              </div>
              {model.id === effectiveModel.id && model.provider === effectiveModel.provider && (
                <CheckIcon className="h-4 w-4 text-claude-accent" />
              )}
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
